import {useCallback} from "react";

import {
    unsetCoeditorVariables,
    useCoeditorAPIContext,
} from "@repo/guide-coeditor-core";
import {
    abortAll,
    AudioCache,
    clearNotificationVariable,
} from "@repo/guide-core-helpers";
import {GuideStateApi} from "@repo/guide-state";

import {useGuideAudioController} from "./use-guide-audio-controller.ts";
import {useInitializeGuide} from "./use-initialize-guide.ts";

interface ResetConversationFn {
    /**
     * Reset the conversation state to the initial state.
     *
     * @param initializeAfterReset If true, the guide will be re-initialized
     * after the conversation is reset.
     */
    (initializeAfterReset?: boolean): Promise<void>;
}

/**
 * Hook to provide a function that will reset the AI guide conversation.
 */
export const useResetConversation = (): ResetConversationFn => {
    const initializeGuide = useInitializeGuide();
    const {setAudioCache, stop: stopAudio} = useGuideAudioController();

    const {reset: resetCoeditor} = useCoeditorAPIContext();

    const resetConversation = useCallback(
        (initializeAfterReset = false) => {
            // Abort any pending requests. This is necessary so that we don't
            // continue processing requests that come in after we've already
            // reset.
            abortAll();

            // Stop any audio that is currently playing and clear the audio
            // cache from the previous conversation.
            stopAudio();
            setAudioCache(new AudioCache());

            GuideStateApi.set("conversation", []);
            GuideStateApi.set("conversationEntries", []);
            GuideStateApi.set("threadId", null);
            GuideStateApi.set("ratedInteractions", new Map());
            clearNotificationVariable("notificationConversation");
            resetCoeditor();
            unsetCoeditorVariables();
            GuideStateApi.set("essaySessionId", null);

            GuideStateApi.set("initializationPromise", null);
            GuideStateApi.set("initialized", false);

            GuideStateApi.set("images", []);

            return initializeAfterReset ? initializeGuide() : Promise.resolve();
        },
        [resetCoeditor, stopAudio, initializeGuide, setAudioCache],
    );
    return resetConversation;
};
